import React, { useState } from 'react';
import {
  Card,
  Title,
  Text,
  TextInput,
  Button,
  Select,
  SelectItem,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Badge
} from '@tremor/react';

export function TeamInvitePage() {
  const [inviteEmail, setInviteEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState('BRAND_STAFF');
  const [inviteLink, setInviteLink] = useState('');
  const [pendingInvites, setPendingInvites] = useState([
    {
      id: 1,
      email: 'pending@example.com',
      role: 'BRAND_STAFF',
      status: 'pending',
      invitedAt: '2024-03-20',
      expiresAt: '2024-03-27'
    }
  ]);

  const roles = [
    { value: 'BRAND_STAFF', label: 'Brand Staff' },
    { value: 'WAREHOUSE_STAFF', label: 'Warehouse Staff' },
    { value: 'SUPPORT_STAFF', label: 'Support Staff' }
  ];

  const handleEmailInvite = async () => {
    try {
      // TODO: Implement email invite API call
      console.log('Sending invite to:', inviteEmail, 'with role:', selectedRole);
      // Add to pending invites
      setPendingInvites(prev => [...prev, {
        id: Date.now(),
        email: inviteEmail,
        role: selectedRole,
        status: 'pending',
        invitedAt: new Date().toISOString().split('T')[0],
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      }]);
      setInviteEmail('');
    } catch (error) {
      console.error('Failed to send invite:', error);
    }
  };

  const generateInviteLink = async () => {
    try {
      // TODO: Implement invite link generation API call
      const newLink = `https://admin.pentatonic.io/join?token=${Math.random().toString(36).substring(7)}&role=${selectedRole}`;
      setInviteLink(newLink);
    } catch (error) {
      console.error('Failed to generate invite link:', error);
    }
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    // TODO: Show success toast
  };

  const revokeInvite = async (inviteId) => {
    try {
      // TODO: Implement revoke invite API call
      setPendingInvites(prev => prev.filter(invite => invite.id !== inviteId));
    } catch (error) {
      console.error('Failed to revoke invite:', error);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Title>Team Invitations</Title>
        <Text>Invite new team members to join your admin portal</Text>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Email Invitation */}
        <Card>
          <Title>Invite by Email</Title>
          <div className="mt-4 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Email Address</label>
              <TextInput
                placeholder="colleague@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Role</label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                {roles.map((role) => (
                  <SelectItem key={role.value} value={role.value}>
                    {role.label}
                  </SelectItem>
                ))}
              </Select>
            </div>
            <Button onClick={handleEmailInvite} disabled={!inviteEmail}>
              Send Invitation
            </Button>
          </div>
        </Card>

        {/* Invite Link Generation */}
        <Card>
          <Title>Generate Invite Link</Title>
          <div className="mt-4 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Role for Invite Link</label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                {roles.map((role) => (
                  <SelectItem key={role.value} value={role.value}>
                    {role.label}
                  </SelectItem>
                ))}
              </Select>
            </div>
            <Button onClick={generateInviteLink}>
              Generate New Link
            </Button>
            {inviteLink && (
              <div className="mt-4">
                <div className="flex items-center space-x-2">
                  <TextInput
                    value={inviteLink}
                    readOnly
                    className="flex-1"
                  />
                  <Button variant="secondary" onClick={copyInviteLink}>
                    Copy
                  </Button>
                </div>
                <Text className="text-sm text-tremor-content-subtle mt-2">
                  This link will expire in 7 days
                </Text>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Pending Invitations */}
      <Card>
        <Title>Pending Invitations</Title>
        <Table className="mt-4">
          <TableHead>
            <TableRow>
              <TableHeaderCell>Email</TableHeaderCell>
              <TableHeaderCell>Role</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>Invited At</TableHeaderCell>
              <TableHeaderCell>Expires At</TableHeaderCell>
              <TableHeaderCell>Actions</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pendingInvites.map((invite) => (
              <TableRow key={invite.id}>
                <TableCell>{invite.email}</TableCell>
                <TableCell>
                  <Badge size="sm">
                    {roles.find(r => r.value === invite.role)?.label || invite.role}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge color="yellow" size="sm">
                    {invite.status}
                  </Badge>
                </TableCell>
                <TableCell>{invite.invitedAt}</TableCell>
                <TableCell>{invite.expiresAt}</TableCell>
                <TableCell>
                  <Button
                    size="xs"
                    variant="secondary"
                    color="red"
                    onClick={() => revokeInvite(invite.id)}
                  >
                    Revoke
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
} 